/**
 * Lightweight pages for the patient portal V1.
 *
 *   Tasks      → Spec H (pre-session workflow) — WIRED to /api/pre-session/me/assignments
 *   Summaries  → Spec E (dual-summary generator + share-with-client toggle)
 *   Documents  → Phase 2
 *   Settings   → minimal account view + sign-out (covered in shell)
 */

import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Stack, Chip, Button, LinearProgress,
  Divider, Alert, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { rawApiClient as apiClient } from '../../services/api';
import AssessmentRunner from '../../components/assessments/AssessmentRunner';

function EmptyState({ title, description, comingFromSpec }) {
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>{title}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Nothing here yet — your therapist will share things here when they're ready.
            </Typography>
            {comingFromSpec && (
              <Box>
                <Chip label={`Coming with ${comingFromSpec}`} size="small" variant="outlined" />
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

/** True when every required task in every assignment is complete (and there was at least one). */
function allRequiredTasksDone(assignments) {
  let seen = 0;
  for (const a of assignments || []) {
    const completionsById = new Map((a.taskCompletions || []).map(c => [String(c.taskId), c]));
    for (const t of (a.templateSnapshot?.tasks || [])) {
      if (t.required === false) continue;
      seen += 1;
      if (completionsById.get(String(t._id))?.status !== 'completed') return false;
    }
  }
  return seen > 0;
}

export function PortalTasks() {
  const ctx = useOutletContext() || {};
  const token = ctx.token || localStorage.getItem('token');
  const navigate = useNavigate();
  // AIMHI Direct only: a client still completing intake is sent back to the
  // stage card once the last questionnaire is in, so the next step (booking)
  // is in front of them. Other portal clients stay on this page as before.
  const intakeInProgress = ctx.user?.effectiveEnrolmentStatus === 'pending_intake';
  const [assignments, setAssignments] = useState(null);
  const [error, setError] = useState(null);
  const [savingTaskId, setSavingTaskId] = useState(null);

  // Active task launcher state — one of:
  //   { kind: 'assessment', assignmentId, taskId, assessmentKey, title }
  //   { kind: 'reading',    assignmentId, taskId, content, title }
  const [active, setActive] = useState(null);
  // Assessment results are stored against a Conversation, which a human-led
  // onboarding client has not started yet. Resolve (and create) theirs so the
  // intake questionnaires can be submitted before they ever open chat.
  const [conversationId, setConversationId] = useState(null);

  async function load() {
    try {
      const res = await apiClient.get('/pre-session/me/assignments');
      const list = res.data?.assignments || [];
      setAssignments(list);
      return list;
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 401) {
        setAssignments([]);
      } else {
        setError(err?.message || 'Could not load tasks');
        setAssignments([]);
      }
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    apiClient.get('/portal/me/record-conversation')
      .then(res => setConversationId(res.data?.conversationId || null))
      .catch(err => console.error('[portal] could not resolve record conversation', err?.message));
  }, []);

  async function markComplete(assignmentId, taskId, result = null) {
    setSavingTaskId(taskId);
    try {
      await apiClient.post(
        `/pre-session/me/assignments/${assignmentId}/tasks/${taskId}/complete`,
        { status: 'completed', ...(result ? { result } : {}) }
      );
      const fresh = await load();
      if (intakeInProgress && allRequiredTasksDone(fresh)) {
        try { await ctx.refreshUser?.(); } catch (_) { /* the home page refreshes again on mount */ }
        navigate('/portal', { state: { intakeTasksComplete: true } });
      }
    } catch (err) {
      setError(err?.message || 'Could not save');
    } finally {
      setSavingTaskId(null);
    }
  }

  function startTask(assignmentId, task) {
    if (task.type === 'assessment' && task.assessmentKey) {
      setActive({
        kind: 'assessment',
        assignmentId,
        taskId: String(task._id),
        assessmentKey: task.assessmentKey,
        title: task.title || task.assessmentKey
      });
    } else if (task.type === 'reading') {
      setActive({
        kind: 'reading',
        assignmentId,
        taskId: String(task._id),
        content: task.readingContent || '',
        title: task.title || 'Reading'
      });
    } else {
      // acknowledgement / unknown → just mark complete
      markComplete(assignmentId, String(task._id));
    }
  }

  async function handleAssessmentComplete(submission) {
    if (!active) return;
    const submitted = submission?.submittedResult || submission;
    const result = submitted ? {
      score: submitted.score ?? null,
      interpretation: submitted.interpretation ?? null,
      submissionId: submitted._id ? String(submitted._id) : null
    } : null;
    await markComplete(active.assignmentId, active.taskId, result);
    setActive(null);
  }

  async function handleReadingComplete() {
    if (!active) return;
    await markComplete(active.assignmentId, active.taskId);
    setActive(null);
  }

  if (assignments === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight={600}>Tasks</Typography>
        <LinearProgress />
      </Stack>
    );
  }

  if (assignments.length === 0) {
    return (
      <EmptyState
        title="Tasks"
        description="Forms or screeners your therapist has asked you to complete before your session."
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>Tasks</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Things your therapist has asked you to do before your next session.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {assignments.map((a) => {
        const tasks = a.templateSnapshot?.tasks || [];
        const completionsById = new Map((a.taskCompletions || []).map(c => [String(c.taskId), c]));
        const totalRequired = tasks.filter(t => t.required !== false).length;
        const doneRequired = tasks.filter(t => {
          if (t.required === false) return false;
          const c = completionsById.get(String(t._id));
          return c?.status === 'completed';
        }).length;

        return (
          <Card variant="outlined" key={a._id}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{a.templateSnapshot?.name || 'Pre-session tasks'}</Typography>
                <Chip
                  size="small"
                  label={`${doneRequired} of ${totalRequired} complete`}
                  color={doneRequired === totalRequired ? 'success' : 'default'}
                />
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {a.status === 'completed' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  You have finished these. Your therapist will go through them with you at your
                  appointment — there is nothing else to do here.
                </Alert>
              )}
              <Stack spacing={1.5}>
                {tasks.sort((x, y) => (x.order || 0) - (y.order || 0)).map((t) => {
                  const completion = completionsById.get(String(t._id));
                  const done = completion?.status === 'completed';
                  return (
                    <Stack
                      key={String(t._id)}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        {done
                          ? <CheckCircleIcon color="success" />
                          : <RadioButtonUncheckedIcon color="action" />}
                        <Box>
                          <Typography variant="subtitle1">
                            {t.title}
                            {t.required === false && (
                              <Chip label="optional" size="small" variant="outlined" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          {t.description && (
                            <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                          )}
                        </Box>
                      </Stack>
                      {!done && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            savingTaskId === String(t._id) ||
                            // Don't let a questionnaire open before its record
                            // conversation resolves — submission would fail.
                            (t.type === 'assessment' && !conversationId)
                          }
                          onClick={() => startTask(a._id, t)}
                        >
                          {savingTaskId === String(t._id)
                            ? 'Saving…'
                            : t.type === 'assessment' ? 'Start questionnaire'
                              : t.type === 'reading'  ? 'Read'
                              : 'Mark complete'}
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      {/* Assessment runner — shown when an assessment task is launched */}
      {active?.kind === 'assessment' && (
        <AssessmentRunner
          token={token}
          testKey={active.assessmentKey}
          conversationId={conversationId}
          // Conversation.assessmentResults.initiatedBy enum — the client is
          // completing their own intake battery, not responding to a prompt.
          initiatedBy="client_self_initiated"
          onComplete={handleAssessmentComplete}
          onCloseRequest={() => setActive(null)}
        />
      )}

      {/* Reading content dialog */}
      {active?.kind === 'reading' && (
        <Dialog open onClose={() => setActive(null)} maxWidth="md" fullWidth>
          <DialogTitle>{active.title}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {active.content || '(No content provided)'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActive(null)}>Close</Button>
            <Button variant="contained" onClick={handleReadingComplete}>
              I&apos;ve read this
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

export function PortalSummaries() {
  const [summaries, setSummaries] = useState(null);
  const [error, setError] = useState(null);

  async function load() {
    try {
      const res = await apiClient.get('/portal/me/summaries');
      setSummaries(res.data?.summaries || []);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 401) {
        setSummaries([]);
      } else {
        setError(err?.message || 'Could not load summaries');
        setSummaries([]);
      }
    }
  }

  useEffect(() => { load(); }, []);

  if (summaries === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" fontWeight={600}>Session summaries</Typography>
        <LinearProgress />
      </Stack>
    );
  }

  if (summaries.length === 0) {
    return (
      <EmptyState
        title="Session summaries"
        description="Plain-language recaps your therapist has chosen to share with you after a session."
      />
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>Session summaries</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          Plain-language recaps your therapist has shared with you. The therapist's clinical note is kept separately.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {summaries.map((s) => (
        <Card variant="outlined" key={s.liveCallId}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h6">{s.therapistName}</Typography>
              <Chip
                size="small"
                label={s.callStartedAt ? new Date(s.callStartedAt).toLocaleDateString() : 'Recent session'}
              />
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
              {s.text}
            </Typography>
            {s.sharedAt && (
              <Typography variant="caption" color="text.secondary">
                Shared {new Date(s.sharedAt).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

export function PortalDocuments() {
  return (
    <EmptyState
      title="Documents"
      description="Consent forms, worksheets, and reading material your therapist has shared."
      comingFromSpec="Phase 2"
    />
  );
}

export function PortalSettings() {
  const { user } = useOutletContext();
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={600}>Account</Typography>
      </Box>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body2">{user?.name || '—'}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body2">{user?.email || '—'}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Mode</Typography>
              <Chip label="Patient portal" size="small" color="primary" variant="outlined" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Typography variant="caption" color="text.secondary">
        To change your password or update contact details, please ask your therapist or clinic.
      </Typography>
    </Stack>
  );
}
